import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import PermissionProtectedRoute from "../../aAdminTabTeam2/routes/PermissionProtectedRoute";
import { useGetProgramDetailById } from "../../api/services/program/programApis";
import AccessDenied from "../../components/accessDenied/AccessDenied";
import { PAGE_IDS, PAGE_URLS } from "../../helpers/constants";
import {
  checkIsStatusCode403Forbidden,
  getValueOrBlank,
  isNotEmpty,
} from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { updateProgramCreatedDate } from "../../redux/slices/program/programSlice";
import CreateProgram from "../createProgram/CreateProgram";
import {
  ProgramTabFormKey,
  ProgramTabFormType,
} from "../createProgram/modelsAndSchemas/Models";

export const pageId = PAGE_IDS.EDIT_PROGRAM;

const EditProgram = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { commonLabels } = useSiteCore();
  const location = useLocation();
  const tabValue = location.state?.tabValue ?? 0;
  const isFromTransfer = location.state?.isTransferedProgram ?? false;
  const isFromEditProjectChildPrograms =
    location?.state?.from === PAGE_URLS.EDIT_PROJECT ||
    location?.state?.from === PAGE_URLS.CREATE_PROJECT ||
    location?.state?.from === PAGE_URLS.VIEW_PROJECT;

  const [programTabValues, setProgramTabValues] =
    useState<ProgramTabFormType>();
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const {
    data: programDetails,
    isFetching: isViewProgramDetailFetching,
    isError: isViewProgramDetailError,
    error: viewProgramDetailError,
    refetchWithParams: refetchViewProgramDetail,
    isSuccess: isProgramDetailSuccess,
  } = useGetProgramDetailById();

  useEffect(() => {
    if (id) {
      setShowAccessDenied(false);
      refetchViewProgramDetail(id);
    }
  }, [id]);

  useEffect(() => {
    if (isProgramDetailSuccess && isNotEmpty(programDetails)) {
      dispatch(updateProgramCreatedDate(programDetails?.createdDate));
      // Check country-level access from isEditable field
      setShowAccessDenied(programDetails?.isEditable !== true);
    }
  }, [
    isProgramDetailSuccess,
    programDetails?.createdDate,
    programDetails?.isEditable,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isGetViewProgramDetailByIdLoading: {
          isVisible: isViewProgramDetailFetching,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isGetViewProgramDetailByIdLoading: { isVisible: false },
        }),
      );
    };
  }, [isViewProgramDetailFetching]);

  useEffect(() => {
    if (isViewProgramDetailError && viewProgramDetailError) {
      // Handle different status codes
      if (checkIsStatusCode403Forbidden(viewProgramDetailError as AxiosError)) {
        setShowAccessDenied(true);
      } else {
        setShowAccessDenied(false);
        dispatch(
          toggleToast({
            open: true,
            message: commonLabels.someThingWentWrongPleaseTryAgainLater,
            type: "error",
          }),
        );
      }

      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isViewProgramDetailError, viewProgramDetailError]);

  useEffect(() => {
    if (isNotEmpty(programDetails)) {
      const defaultProgramValues = {
        [ProgramTabFormKey.prefix]: getValueOrBlank(programDetails?.prefixKey),
        [ProgramTabFormKey.firstName]: getValueOrBlank(
          programDetails?.firstName,
        ),
        [ProgramTabFormKey.lastName]: getValueOrBlank(programDetails?.lastName),
        [ProgramTabFormKey.programName]: getValueOrBlank(
          programDetails?.programName,
        ),
        [ProgramTabFormKey.site]: getValueOrBlank(programDetails?.site),
        [ProgramTabFormKey.poNumber]: getValueOrBlank(
          programDetails?.purchaseOrderId,
        ),
        [ProgramTabFormKey.clientInformation1]: getValueOrBlank(
          programDetails?.clientInformation1,
        ),
        [ProgramTabFormKey.clientInformation2]: getValueOrBlank(
          programDetails?.clientInformation2,
        ),
        [ProgramTabFormKey.clientInformation3]: getValueOrBlank(
          programDetails?.clientInformation3,
        ),
        [ProgramTabFormKey.dossierNumber]: getValueOrBlank(
          programDetails?.dossierNumber,
        ),
        [ProgramTabFormKey.actualClientId]: getValueOrBlank(
          programDetails?.actualClientId,
        ),
        [ProgramTabFormKey.businessApprovalNumber]: getValueOrBlank(
          programDetails?.businessApprovalNumber,
        ),
        [ProgramTabFormKey.clientRelationShipOwner]: getValueOrBlank(
          programDetails?.clientRelationShipOwnerId,
        ),
        [ProgramTabFormKey.deliveryOffice]: getValueOrBlank(
          programDetails?.deliveryOfficeId,
        ),
        [ProgramTabFormKey.billingContact]: getValueOrBlank(
          programDetails?.billingContactId,
        ),
        [ProgramTabFormKey.hrContact]: getValueOrBlank(
          programDetails?.hrContactId,
        ),
        [ProgramTabFormKey.projectManager]: getValueOrBlank(
          programDetails?.projectManagerId,
        ),
        [ProgramTabFormKey.projectCoordinator]: getValueOrBlank(
          programDetails?.projectCoordinatorId,
        ),
        [ProgramTabFormKey.costCentre]: getValueOrBlank(
          programDetails?.costCenter,
        ),

        [ProgramTabFormKey.solutionOffering]: "",
        [ProgramTabFormKey.programClass]: "",
        [ProgramTabFormKey.businessUnit]: "",
        [ProgramTabFormKey.billingOffice]: "",
        [ProgramTabFormKey.salesOffice]: "",

        programStatus: getValueOrBlank(programDetails?.programStatus),
        [ProgramTabFormKey.salesCreditDetails]:
          programDetails?.salesCreditDetails ?? [],
        [ProgramTabFormKey.deliveryLeadEngagementManagerId]: getValueOrBlank(
          programDetails?.deliveryLeadEngagementManager?.id,
        ),
        [ProgramTabFormKey.additionalHRContactId]: getValueOrBlank(
          programDetails?.additionalHRContact?.id,
        ),
        [ProgramTabFormKey.additionalInvoiceContactId]: getValueOrBlank(
          programDetails?.additionalInvoiceContact?.id,
        ),
        [ProgramTabFormKey.hiringManager]: getValueOrBlank(
          programDetails?.hiringManager,
        ),
        [ProgramTabFormKey.participantManagerId]: getValueOrBlank(
          programDetails?.participantManager?.id,
        ),
      };
      setProgramTabValues(defaultProgramValues);
    }
  }, [programDetails]);

  // Prevent rendering CreateProgram while access is denied or detail request is unresolved
  if (showAccessDenied) {
    return <AccessDenied />;
  }

  return (
    <PermissionProtectedRoute
      hasCountryLevelAccess={programDetails?.isEditable}
    >
      <CreateProgram
        pageId={pageId}
        isEditProgram={true}
        programId={id}
        editProgramData={programTabValues}
        tabValue={tabValue}
        isFromTransfer={isFromTransfer}
        isFromEditProjectChildPrograms={isFromEditProjectChildPrograms}
      />
    </PermissionProtectedRoute>
  );
};

export default EditProgram;
